import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { GlobalErrorHandler } from './shared/services/chunk-handler.service';
import { provideRouter, withHashLocation } from '@angular/router';
import { routes } from './app.routes';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LoaderModule } from './shared/loader/loader.module';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalService,
} from '@azure/msal-angular';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from './core/msal/msal.config';
import { TokenInterceptor } from './core/interceptors/token-interceptor.interceptor';
import { AuthGuard } from './core/guards/auth.guard';
import { LoginGuard } from './core/guards/login.guards';
import { IframeCommunication } from './features/configurator/service/iframe-communication';
import { isInsideIframe, waitForHostToken } from './iframe-helper';
import { BrowserAuthError, BrowserUtils, InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { LoaderService } from './shared/loader/loader.service';
import { IframeAuthService } from './core/services/iframe-auth';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideRouter(routes),
    importProvidersFrom(LoaderModule),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },

    provideAppInitializer(async () => {
      const msal = inject(MsalService);
      const loader = inject(LoaderService);
      const iframeAuth = inject(IframeAuthService);
      const inIframe = isInsideIframe();
      await msal.instance.initialize();

      if (inIframe) {
        // No MSAL interactive flow at all — just consume host token
        const payload = await waitForHostToken(8000);

        if (payload?.access_token) {
          console.log('Iframe - Received host token');
          iframeAuth.setTokenFromHost(payload);
        } else {
          console.error('No token from host — iframe cannot authenticate independently');
          iframeAuth.isLoading.set(false);
          // Show an error state, NOT a login button (login can't work in sandbox)
        }
        return;
      }

      const redirectResult = await msal.instance.handleRedirectPromise();
      if (redirectResult?.account) {
        msal.instance.setActiveAccount(redirectResult.account);
        return;
      }

      const cachedAccounts = msal.instance.getAllAccounts();
      if (cachedAccounts.length > 0) {
        msal.instance.setActiveAccount(cachedAccounts[0]);
        return;
      }
    }),
    MsalService,
    MsalBroadcastService,
    // MsalGuard,
    AuthGuard,
    LoginGuard,
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json?t=06102026',
      }),
      fallbackLang: 'en',
      lang: 'en',
    }),
  ],
};
